import { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Button,
    Icon,
    Accordion,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import { APP_REGISTER_URL } from "../../../services/constants";
import { fetchPricingTiers, fetchFaqsBySection } from "../../../services/strapi/queries";
import type { PricingTier, FaqItem } from "../../../services/strapi/types";

import {
    DoodleUnderline,
    DoodleSparkle,
    WashiTape,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";

interface PricingPlan {
    name: string;
    tagline?: string;
    price: string;
    priceNote?: string;
    features: string[];
    cta: string;
    primary: boolean;
    action: "register" | "contact";
}

function tierToPlan(t: PricingTier): PricingPlan {
    return {
        name: t.name,
        tagline: t.tagline,
        price: t.price,
        priceNote: t.priceNote,
        features: t.features ?? [],
        cta: t.ctaLabel ?? "Get started",
        primary: !!t.primary,
        action: t.ctaAction ?? "register",
    };
}

const plans = [
    {
        name: "Free",
        tagline: "For small schools",
        price: "Free",
        priceNote: "Less than 50 students",
        features: [
            "Full platform access",
            "Unlimited result entries",
            "Report card generation",
            "Parent portal",
            "₦100 processor fee per transaction",
        ],
        cta: "Get started",
        primary: false,
        action: "register" as const,
    },
    {
        name: "Tier 1",
        tagline: "For growing schools",
        price: "₦5,000",
        priceNote: "per month · 50–100 students",
        features: [
            "Everything in Free",
            "Priority email support",
            "Advanced analytics",
            "Bulk export tools",
            "₦100 processor fee per transaction",
        ],
        cta: "Get started",
        primary: false,
        action: "register" as const,
    },
    {
        name: "Tier 2",
        tagline: "For established schools",
        price: "₦10,000",
        priceNote: "per month · 100–200 students",
        features: [
            "Everything in Tier 1",
            "Advanced analytics dashboard",
            "Bulk report card generation",
            "Staff role management",
            "₦100 processor fee per transaction",
        ],
        cta: "Get started",
        primary: true,
        action: "register" as const,
    },
    {
        name: "Tier 3",
        tagline: "For large institutions",
        price: "₦30,000",
        priceNote: "per month · 200+ students",
        features: [
            "Everything in Tier 2",
            "Dedicated account manager",
            "Custom onboarding session",
            "Priority phone support",
            "₦100 processor fee per transaction",
        ],
        cta: "Contact us",
        primary: false,
        action: "contact" as const,
    },
];

const faqs = [
    {
        q: "Is there a free trial?",
        a: "Schools with fewer than 50 students can use Result completely free — forever, not just a trial. Larger schools can start with a 30-day trial before choosing a paid plan.",
    },
    {
        q: "What is the ₦100 processor fee?",
        a: "Every financial transaction processed through Result (fee payments, refunds) carries a flat ₦100 processor fee charged to the school. This is separate from your monthly subscription.",
    },
    {
        q: "Can I change plans later?",
        a: "Yes. You can upgrade or downgrade your plan at any time. Changes take effect at the start of the next billing cycle.",
    },
    {
        q: "How is the student count measured?",
        a: "Your student count is based on the number of active students in your account at the time of billing. Archived or graduated students are not counted.",
    },
    {
        q: "Do you offer discounts for groups of schools?",
        a: "Yes — if you manage multiple schools or represent a school group, contact us for group pricing. We handle multi-school setups with a shared admin dashboard.",
    },
    {
        q: "What payment methods do you accept?",
        a: "Subscriptions can be paid by bank transfer or card. All payments are processed securely through Result Finance.",
    },
];

import { usePageTitle } from "../../../hooks/usePageTitle";

const PricingPage = () => {
    usePageTitle(
        "Simple & Transparent Pricing",
        "Free for schools under 50 students. Predictable, affordable pricing plans for growing Nigerian primary and secondary schools."
    );
    const navigate = useNavigate();
    const [activePlans, setActivePlans] = useState<PricingPlan[]>(plans);
    const [activeFaqs, setActiveFaqs] = useState<{ q: string; a: string }[]>(faqs);

    useEffect(() => {
        let cancelled = false;
        Promise.all([fetchPricingTiers(), fetchFaqsBySection("pricing")])
            .then(([tiers, faqDocs]) => {
                if (cancelled) return;
                if (tiers.length > 0) setActivePlans(tiers.map(tierToPlan));
                if (faqDocs.length > 0)
                    setActiveFaqs(
                        faqDocs.map((it: FaqItem) => ({ q: it.question, a: it.answer })),
                    );
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[16, 24]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="4xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                Honest &amp; Transparent
                            </Text>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl"]} fontWeight="extrabold" lineHeight="1.15">
                            Simple pricing for{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                every school
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>
                        <Text fontSize={["md", "lg"]} opacity={0.8} maxW="2xl">
                            Start completely free if you have under 50 students. Scale whenever you are ready.
                            A flat ₦100 processor fee applies to all transactions.
                        </Text>
                    </VStack>
                </Container>
            </Box>

            {/* Plans */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="7xl">
                    <SimpleGrid columns={[1, 2, 4]} gap={6} alignItems="stretch">
                        {activePlans.map((plan, idx) => (
                            <Box
                                key={plan.name}
                                p={7}
                                borderRadius="2xl"
                                border="2.5px solid #0A3D2E"
                                bg={plan.primary ? "#FFFBEB" : "white"}
                                position="relative"
                                boxShadow={plan.primary ? "6px 8px 0px #0A3D2E" : "3px 4px 0px #0A3D2E"}
                                style={{
                                    transform: plan.primary ? "scale(1.03) rotate(-0.5deg)" : idx % 2 === 0 ? "rotate(-0.5deg)" : "rotate(0.5deg)",
                                }}
                                transition="all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
                                _hover={{
                                    transform: "translate(-3px, -3px) scale(1.04)",
                                    boxShadow: "7px 9px 0px #0A3D2E",
                                }}
                            >
                                {plan.primary ? (
                                    <WashiTape
                                        angle={-2}
                                        top="-14px"
                                        left="50%"
                                        width="130px"
                                        transform="translateX(-50%)"
                                        tapeColor="#FEF08A"
                                        label="MOST POPULAR 🎉"
                                    />
                                ) : idx === 0 ? (
                                    <DoodlePaperclip color="#10B981" size={32} position="absolute" top="-12px" right="24px" />
                                ) : null}

                                <VStack align="start" gap={5} h="full" pt={plan.primary ? 2 : 0}>
                                    <Box>
                                        <Text fontSize="xl" fontWeight="extrabold" color="gray.900">{plan.name}</Text>
                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">{plan.tagline}</Text>
                                    </Box>
                                    <Box position="relative">
                                        <Text fontSize="3xl" fontWeight="extrabold" color="brand.800">{plan.price}</Text>
                                        <Text fontSize="xs" color="gray.500" mt={0.5}>{plan.priceNote}</Text>
                                        {plan.name === "Free" && (
                                            <Box
                                                className="font-handwriting"
                                                fontSize="sm"
                                                fontWeight="bold"
                                                color="emerald.700"
                                                mt={1}
                                            >
                                                Forever free! ✨
                                            </Box>
                                        )}
                                    </Box>
                                    <VStack align="start" gap={2.5} flex={1} w="full">
                                        {plan.features.map((f) => (
                                            <HStack key={f} gap={2} align="start">
                                                <Icon as={FaCheckCircle} color="brand.700" fontSize="xs" mt={1} flexShrink={0} />
                                                <Text fontSize="xs" fontWeight="medium" color="gray.700">{f}</Text>
                                            </HStack>
                                        ))}
                                    </VStack>
                                    <Button
                                        w="full"
                                        size="md"
                                        bg={plan.primary ? "brand.800" : "white"}
                                        color={plan.primary ? "white" : "brand.800"}
                                        border="2px solid #0A3D2E"
                                        boxShadow="2px 2px 0px #0A3D2E"
                                        fontWeight="bold"
                                        borderRadius="xl"
                                        transition="all 0.15s ease"
                                        _hover={{
                                            bg: plan.primary ? "brand.900" : "brand.50",
                                            transform: "translate(-1px, -1px)",
                                            boxShadow: "4px 4px 0px #0A3D2E",
                                        }}
                                        onClick={() =>
                                            plan.action === "contact"
                                                ? navigate("/contact")
                                                : (window.location.href = APP_REGISTER_URL)
                                        }
                                    >
                                        {plan.cta}
                                    </Button>
                                </VStack>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* FAQ */}
            <Box py={24}>
                <Container maxW="3xl">
                    <VStack gap={12}>
                        <VStack gap={4} textAlign="center">
                            <Heading as="h2" fontSize={["2xl", "3xl"]} fontWeight="bold" color="gray.900">
                                Pricing questions answered
                            </Heading>
                            <Text color="gray.600">Everything you need to know before signing up.</Text>
                        </VStack>

                        <Accordion.Root collapsible w="full">
                            {activeFaqs.map((faq, i) => (
                                <Accordion.Item key={i} value={`faq-${i}`} borderBottom="1px solid" borderColor="gray.200">
                                    <Accordion.ItemTrigger py={5}>
                                        <Text fontWeight="semibold" color="gray.900" textAlign="left">{faq.q}</Text>
                                    </Accordion.ItemTrigger>
                                    <Accordion.ItemContent pb={5}>
                                        <Text color="gray.600" lineHeight="1.8">{faq.a}</Text>
                                    </Accordion.ItemContent>
                                </Accordion.Item>
                            ))}
                        </Accordion.Root>
                    </VStack>
                </Container>
            </Box>

            {/* CTA */}
            <Box py={24} bg="brand.900" color="white">
                <Container maxW="3xl">
                    <VStack gap={8} textAlign="center">
                        <Heading as="h2" fontSize={["2xl", "3xl"]} fontWeight="extrabold">
                            Not sure which plan is right for you?
                        </Heading>
                        <Text opacity={0.75} fontSize="lg">
                            Talk to us. We&apos;ll help you figure out the best fit for your school.
                        </Text>
                        <Button size="lg" bg="gold.500" color="brand.950" fontWeight="bold" px={10} _hover={{ bg: "gold.400" }} onClick={() => navigate("/contact")}>
                            Contact us
                        </Button>
                    </VStack>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default PricingPage;
